import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { object } from "valibot";
import { useTranslation } from "../../state/i18n";
import { labelAsName, requireText } from "../../utils/validation/base";
import { MAX_CHARACTERS_XL } from "../../constants";
import { HtmlInput } from "../../components/inputs/HtmlInput";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { HTMLPreview } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { keyFromSelector } from "i18next";

const Schema = object({
  html_code: requireText(MAX_CHARACTERS_XL),
  label_text: labelAsName(),
});

export type HTMLFieldProperties = InferOutput<typeof Schema>;

const HTMLField: Field<HTMLFieldProperties, DefaultDtoType> = {
  type: "html",
  category: "layout",
  widget: "com.moreapps:html:1",
  schema: Schema,
  hasData: false,
  label: "not_required",
  defaultProperties: () => ({ html_code: "<h3>html</h3>" }),
  icon: "code",
  titleKey: keyFromSelector(($) => $.HTML_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.HTML_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_NAME), // Use 'name' to functionally distinguish it from `label_text` for end user
    html_code: keyFromSelector(($) => $.FIELD_PROPERTY_HTML_CODE),
  },
  PreviewContent: (props) => <HTMLPreview value={props.properties.html_code} />,
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <PropertySection>
        <TextInput
          label={t(props.propertyKeys.label_text)}
          value={props.data.properties.label_text ?? undefined}
          onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
          onInput={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
          errorMessage={props.getError("label_text")}
          info={{
            title: t(props.propertyKeys.label_text),
            alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.label_text }),
            description: t(($) => $.FIELD_PROPERTY_NAME_DESCRIPTION),
          }}
        />
        <HtmlInput
          value={props.data.properties.html_code}
          required
          label={t(props.propertyKeys.html_code)}
          onChange={(value) => props.setValue("html_code", value)}
          errorMessage={props.getError("html_code")}
        />
      </PropertySection>
    );
  },
};

export default initField(HTMLField);
